import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Stack,
  Table,
  Dialog,
  Button,
  Divider,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField, RHFUploadBox } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import TransactionSelectDialog, {
  TransactionCard,
} from 'src/components/ui-kit/transaction-select-dialog';
import { useBoolean } from 'src/hooks/use-boolean';
import useCurrentDateTime from 'src/hooks/use-current-date-time';
import { uploadImage } from 'src/libs/firebase/firebase-upload';
import { RedeemTxType } from 'src/type/gold-withdraw';
import { PositionTxType } from 'src/type/position';
import { TransactionType } from 'src/type/transaction';
import axios, { endpoints } from 'src/utils/axios';
import { convertGold } from 'src/utils/convert';
import { toastError } from 'src/utils/format-error';
import { fData, fNumber } from 'src/utils/format-number';
import * as Yup from 'yup';

const Schema = Yup.object().shape({
  reason: Yup.string(),
  // redeemImage: Yup.mixed<File>().required('กรุณาอัพโหลดรูปหลักฐาน'),
  redeemImage: Yup.mixed<File>().notRequired(),
});

const defaultValues = {
  reason: '',
  redeemImage: null as unknown as File,
};

type Props = {
  open: boolean;
  isCreateAndApprove?: boolean;
  onClose: VoidFunction;
  tx: RedeemTxType;
  handleReload: VoidFunction;
};

export default function RedeemApproveDialog({
  open,
  onClose,
  tx,
  isCreateAndApprove = false,
  handleReload,
}: Props) {
  const currentDateTime = useCurrentDateTime();

  const select = useBoolean(false);

  const [txFilters, setTxFilters] = useState<PositionTxType[]>(tx.refTxs?.length ? tx.refTxs : []);
  const [totalWeight, setTotalWeight] = useState(0);

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (tx.type !== TransactionType.REDEEM) {
        throw new Error('ไม่สามารถอัพเดทข้อมูลได้ เนื่องจากเป็นการถอนทอง');
      }

      // if (tx.status !== TransactionStatus.PENDING) {
      //   throw new Error('ไม่สามารถอัพเดทข้อมูลได้ เนื่องจากไม่ได้เป็นสถานะรอดำเนินการ');
      // }

      // if (!values.redeemImage) {
      //   throw new Error('กรุณาอัพโหลดรูปหลักฐาน');
      // }
      let redeemImageUrl = null;
      if (values.redeemImage) {
        redeemImageUrl = await uploadImage(values.redeemImage, 'images');
      }

      await axios.post(endpoints.transactions.approveRedeem(tx.id), {
        note: values.reason,
        ...(redeemImageUrl && { redeemImageUrl }),
        currentDateTime,
        refTxIds: txFilters.map((item) => item.id).join(','),
      });
      if (!isCreateAndApprove) {
        handleReload();
      }
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    if (isCreateAndApprove) {
      handleReload();
    }
    reset(defaultValues);
    onClose();
  };

  const handleDropImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('redeemImage', newFile, { shouldValidate: true, shouldTouch: true });
      }
    },
    [setValue]
  );

  const handleSelectTxs = (items: PositionTxType[]) => {
    const isTxGram = tx.unitName === 'กรัม';

    setTxFilters(items);
    select.onFalse();
    const total = items.reduce((acc, item) => {
      let remainGold = item.remainGoldAmount;

      switch (true) {
        case isTxGram && item.unitName === 'กรัม':
        case !isTxGram && item.unitName === 'บาท':
          break;
        case isTxGram && item.unitName === 'บาท':
          remainGold = convertGold(item.remainGoldAmount, 'weightGrams');
          break;
        case !isTxGram && item.unitName === 'กรัม':
          remainGold = convertGold(item.remainGoldAmount, 'weightBaht');
          break;
        default:
          remainGold = 0;
          break;
      }

      return acc + remainGold;
    }, 0);
    setTotalWeight(total);
  };

  return (
    <>
      <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <DialogTitle>อนุมัติรายการถอนสินค้า</DialogTitle>

          <DialogContent>
            <Stack spacing={3}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell align="center">จำนวน</TableCell>
                      <TableCell align="center">ค่าธรรมเนียม</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {tx?.productRedeemFees?.map((fee) => (
                      <TableRow key={fee.id}>
                        <TableCell>{fee?.productRedeemFee?.name}</TableCell>
                        <TableCell align="center">{fNumber(fee?.amount || 0)}</TableCell>
                        <TableCell align="center">{fNumber(fee?.feeTotal || 0)} THB</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Divider style={{ borderStyle: 'dashed' }} />

                <Table size="small" sx={{ mt: 3 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>น้ำหนักทองทั้งหมด</TableCell>
                      <TableCell align="center">
                        {fNumber(tx?.amount || 0)} {tx.unitName || 'บาท'}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>ค่าธรรมเนียมทั้งหมด</TableCell>
                      <TableCell align="center">{fNumber(tx?.fee || 0)} THB</TableCell>
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>

              <SelectPosition
                tx={tx}
                select={select}
                txFilters={txFilters}
                totalWeight={totalWeight}
              />

              <RHFUploadBox
                name="redeemImage"
                maxSize={3145728}
                onDrop={handleDropImage}
                placeholder="อัพโหลดรูปหลักฐาน"
                sx={{
                  maxHeight: 140,
                  width: '100%',
                  height: '100%',
                  aspectRatio: '1/1',
                  mx: 'auto',
                }}
                helperText={
                  <>
                    อนุญาตไฟล์ *.jpeg, *.jpg, *.png, *.gif
                    <br /> ขนาดสูงสุด {fData(3145728)}
                  </>
                }
              />

              <RHFTextField
                multiline
                rows={3}
                name="reason"
                placeholder="หมายเหตุ"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
              ยกเลิก
            </Button>
            <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
              ดำเนินการต่อ
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Dialog>

      <TransactionSelectDialog
        open={select.value}
        onClose={select.onFalse}
        selectedItems={txFilters}
        onSelects={handleSelectTxs}
        user={tx.user}
        productId={tx.productId}
        productName={tx.product.name}
        unitName={tx.unitName}
      />
    </>
  );
}

function SelectPosition({
  tx,
  select,
  txFilters,
  totalWeight,
}: {
  tx: RedeemTxType;
  select: ReturnType<typeof useBoolean>;
  txFilters: PositionTxType[];
  totalWeight: number;
}) {
  return (
    <Stack spacing={1}>
      {!tx.refTxIds && (
        <Box
          onClick={select.onTrue}
          sx={{
            width: 1,
            height: 54,
            display: 'flex',
            alignItems: 'center',
            border: (theme_) => `1px solid ${theme_.palette.divider}`,
            borderRadius: 1,
            px: 1.5,
            cursor: 'pointer',
          }}
        >
          <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ color: 'text.disabled', ml: 1 }}>
            เลือกรายการตั๋วทอง
          </Typography>
        </Box>
      )}
      {txFilters.length ? (
        <Stack spacing={1}>
          {txFilters.map((item) => (
            <TransactionCard key={item.id} tx={item} onClick={() => {}} disableClick />
          ))}
        </Stack>
      ) : null}

      {!tx.refTxIds && (
        <Stack alignItems="flex-end" sx={{ width: 1 }}>
          <Typography
            variant={totalWeight < tx.amount ? 'subtitle1' : 'subtitle2'}
            color={totalWeight < tx.amount ? 'error' : 'success.main'}
          >
            จำนวนทองก่อนถอน รวม {fNumber(tx.refTxIds ? totalWeight + tx.amount : totalWeight)}{' '}
            {tx.unitName || 'บาท'}
          </Typography>
          <Typography variant="subtitle2">
            {txFilters.length === 0 ? 'จำนวนทองที่ต้องถอน' : 'จำนวนทองที่ถอน'} {fNumber(tx.amount)}{' '}
            {tx.unitName || 'บาท'}
          </Typography>
          {totalWeight - tx.amount >= 0 && (
            <Typography variant="subtitle2">
              จำนวนทองคงเหลือ {fNumber(totalWeight - tx.amount)} {tx.unitName || 'บาท'}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}
